import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { Chip, FormHelperText, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { SEARCH_INTERESTS_LIST } from '../../graphQL/queries/tag';

const GRAD = 'linear-gradient(135deg, #f62722 0%, #ff6b35 100%)';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'calc(100% - 32px)',
  maxWidth: 440,
  maxHeight: '85vh',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

interface InterestModalProps {
  userInterestIds: number[];
  question?: string;
  saveErrorMsg?: string;
  onClose?: () => void;
  // eslint-disable-next-line no-unused-vars
  onSave: (interestIds) => void;
}

export const InterestModal = ({
  question = 'Whats are you interested in?',
  saveErrorMsg = '',
  userInterestIds,
  onClose,
  onSave,
}: InterestModalProps) => {
  const [error, setError] = useState<string>('');
  const [interestIds, setInterestIds] = useState<number[]>([]);

  const { data: interests } = useQuery(SEARCH_INTERESTS_LIST, {
    fetchPolicy: 'no-cache',
  });

  const allInterests = interests?.interests ?? [];

  useEffect(() => {
    const storageInterestIds = localStorage.getItem('interestIds');
    let selectedInterestIds = [];

    if (storageInterestIds) {
      selectedInterestIds = JSON.parse(storageInterestIds);
    }
    if (userInterestIds && userInterestIds.length > 0) {
      selectedInterestIds = [...selectedInterestIds, ...userInterestIds];
    }
    setInterestIds(selectedInterestIds);
  }, [userInterestIds]);

  const onClickInterest = (interestId: number) => {
    setError('');
    if (interestIds.includes(interestId)) {
      setInterestIds(interestIds.filter((id) => id !== interestId));
    } else {
      setInterestIds([...interestIds, interestId]);
    }
  };

  const onSaveProfile = () => {
    if (saveErrorMsg && interestIds.length > 5) {
      setError(saveErrorMsg);
      return;
    }

    onSave(
      interestIds
        .map((interestId) => {
          // Guard against stale ids that no longer exist in the list —
          // otherwise `[0].name` throws and the Save button "does nothing".
          const match = allInterests.find(
            (interest) => interest.id == interestId,
          );
          if (!match) return null;
          return { id: interestId, name: match.name, type: 'interest' };
        })
        .filter(Boolean),
    );
  };

  const count = interestIds.length;

  return (
    <Modal
      open={true}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            px: 3,
            pt: 3,
            pb: 2,
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography
              id="modal-modal-title"
              variant="h6"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.4px',
                lineHeight: 1.25,
              }}
            >
              {question}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Pick a few topics to personalise your feed
            </Typography>
          </Box>
          {onClose && (
            <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Chips — scrollable */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 3,
            py: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignContent: 'flex-start',
          }}
        >
          {allInterests.map((interest) => {
            const isSelected = interestIds.includes(interest.id);
            return (
              <Chip
                key={interest.id}
                label={interest.name}
                onClick={() => onClickInterest(interest.id)}
                sx={{
                  height: 38,
                  borderRadius: '19px',
                  fontSize: '13px',
                  fontWeight: isSelected ? 700 : 500,
                  cursor: 'pointer',
                  background: isSelected ? GRAD : 'rgba(0,0,0,0.05)',
                  color: isSelected ? '#fff' : 'text.primary',
                  border: 'none',
                  boxShadow: isSelected
                    ? '0 2px 10px rgba(246,39,34,0.3)'
                    : 'none',
                  transition: 'all 0.15s',
                  '& .MuiChip-label': { px: 1.75 },
                  '&:hover': {
                    background: isSelected ? GRAD : 'rgba(0,0,0,0.09)',
                  },
                }}
              />
            );
          })}

          {allInterests.length === 0 && (
            <Box sx={{ width: '100%', py: 5, textAlign: 'center' }}>
              <Typography variant="body2" color="text.disabled">
                Loading interests…
              </Typography>
            </Box>
          )}
        </Box>

        {error && (
          <FormHelperText error sx={{ px: 3, mt: 0.5 }}>
            {error}
          </FormHelperText>
        )}

        {/* Save CTA */}
        <Box
          sx={{
            p: 3,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            disabled={count === 0}
            onClick={onSaveProfile}
            sx={{
              borderRadius: '12px',
              py: 1.25,
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '0.95rem',
              background: count > 0 ? GRAD : undefined,
              boxShadow: count > 0 ? '0 4px 14px rgba(246,39,34,0.3)' : 'none',
              '&:hover': {
                boxShadow:
                  count > 0 ? '0 4px 20px rgba(246,39,34,0.45)' : 'none',
              },
            }}
          >
            Save{count > 0 ? ` (${count})` : ''}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
